import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import _ from 'lodash';
import { Box, Typography } from '@mui/material';

// components
import { AccountDetailsFields } from './fields/AccountDetailsFields';
import { AccountFieldsContainer } from './profile/AccountFieldsContainer';

// utils
import { useSingleFormValidationResolver } from '../hooks';
import { useAccountValidationSchema } from './_validations/useAccountValidationSchema';

/* eslint-disable no-template-curly-in-string */

const initialDefaultValue = {
  name: null,
  revenue: null,
  noOfEmployeesAll: null,
  yearEstablished: null,
  ownershipType: null,
  naicsCode: null,
  secondNaicsCode: null,
  primaryNaicsPercentage: null,
  address1: null,
  city: null,
  state: null,
  zipCode: null,
  phoneNumber: null,
  entityType: null,
  isFranchise: null,
};

export const AccountDetailsForm = ({
  formId = 'accountDetails',
  formName = 'Account Details',
  children,
  defaultValues = initialDefaultValue,
  disabled = false,
  handleOnSubmit,
  registerForm = () => null,
  validationSchema,
  reportErrors,
  FieldsContainer = AccountFieldsContainer,
}: any) => {
  const accountValidationSchema = useAccountValidationSchema();
  const resolver = useSingleFormValidationResolver(
    validationSchema ?? accountValidationSchema,
    {
      formId,
      formName,
      reporter: reportErrors,
    }
  );

  const { handleSubmit, ...methods } = useForm<Record<string, any>>({
    defaultValues,
    resolver,
  });

  function onSubmit(values: any) {
    let accountDetailFormData = values;
    if (!values.secondNaicsCode) {
      accountDetailFormData = _.omit(accountDetailFormData, [
        'secondNaicsCode',
        'secondaryNaicsPercentage',
        'secondPrimaryIndustry',
        'secondSecondaryIndustry',
      ]);
    }
    return handleOnSubmit(accountDetailFormData).then((finalValues: any) => {
      const nextValues = finalValues || accountDetailFormData;
      methods.reset({
        ...nextValues,
        secondaryNaicsPercentage: 100 - nextValues.primaryNaicsPercentage,
      });
      return nextValues;
    });
  }

  React.useEffect(() => {
    registerForm({
      id: formId,
      name: formName,
      handler: handleSubmit(onSubmit),
      reset: methods.reset,
    });
    // eslint-disable-next-line
  }, []);

  /* start business logic */
  const initialRender = React.useRef(true);

  // watch naics percentages since they're dynamic
  const primaryNaicsPercentage = methods.watch('primaryNaicsPercentage');
  methods.watch([
    'secondaryNaicsPercentage',
    'primaryIndustry',
    'secondaryIndustry',
    'secondPrimaryIndustry',
    'secondSecondaryIndustry',
    'secondNaicsCode',
  ]);

  React.useEffect(() => {
    if (initialRender.current && primaryNaicsPercentage) {
      methods.reset();
      initialRender.current = false;
    }

    if (!initialRender.current && primaryNaicsPercentage) {
      methods.setValue(
        'secondaryNaicsPercentage',
        100 - Number(primaryNaicsPercentage)
      );
    }
    // eslint-disable-next-line
  }, [primaryNaicsPercentage]);
  /* end business logic */

  return (
    // @ts-expect-error TS(2741): Property 'handleSubmit' is missing in type '{ chil... Remove this comment to see the full error message
    <FormProvider {...methods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        {typeof children === 'function' ? (
          children()
        ) : (
          <>
            <Box display="flex" justifyContent="space-between">
              <Typography
                variant="h3"
                className="primer-text contrast-text"
                paragraph
              >
                {formName}
              </Typography>
            </Box>
            <FieldsContainer>
              <AccountDetailsFields disabled={disabled} />
            </FieldsContainer>
          </>
        )}
      </form>
    </FormProvider>
  );
};
